import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import useFoundationDashboard from '../../../hooks/useFoundationDashboard';
import useAuth from '../../../hooks/useAuth';
import { getIconComponent } from './iconUtils';

// V1 Components - local copies for independent modifications
import LoadingView from './LoadingView';
import ErrorView from './ErrorView';
import EmptyStateView from './EmptyStateView';
import SectionHeader from './SectionHeader';
import TasksGrid from './TasksGrid';
import TaskMenu from './TaskMenu';
import AgentDialog from './AgentDialog';

/**
 * Foundation V1 Component
 * Optimized implementation using single API call
 */
const Foundation = ({ projectId, showNotification, onOpenTaskSelector }) => {
  const { user } = useAuth();
  const containerRef = useRef(null);
  const [contentHeight, setContentHeight] = useState('auto');

  // Orchestrator dialog state
  const [showOrchestratorDialog, setShowOrchestratorDialog] = useState(false);
  const [selectedTaskForOrchestrator, setSelectedTaskForOrchestrator] = useState(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTile, setSelectedTile] = useState(null);

  // Get dashboard data
  const {
    project,
    sections,
    tasksWithConversations,
    sectionAvatars,
    isLoading,
    error,
    refetch,
    hasConversation,
    isTaskCompleted,
    toggleTaskStatus,
    updateOrchestrator
  } = useFoundationDashboard(projectId);

  // Calculate dynamic content height
  useEffect(() => {
    const calculateContentHeight = () => {
      const mainHeaderHeight = 73;
      const foundationHeaderHeight = 80;
      const additionalPadding = 100;
      const windowHeight = window.innerHeight;
      const availableHeight = windowHeight - mainHeaderHeight - foundationHeaderHeight - additionalPadding;
      const finalHeight = Math.max(availableHeight, 400);
      setContentHeight(`${finalHeight}px`);
    };

    calculateContentHeight();
    window.addEventListener('resize', calculateContentHeight);
    return () => window.removeEventListener('resize', calculateContentHeight);
  }, []);

  // Transform sections data to match expected format
  const groupedTiles = useMemo(() => {
    const groups = {};

    sections.forEach((section) => {
      const sectionName = section.name;
      if (!groups[sectionName]) {
        groups[sectionName] = [];
      }

      section.tasks.forEach((task) => {
        // Determine task status
        let status = 'available';
        if (isTaskCompleted(task.id)) {
          status = 'completed';
        } else if (hasConversation(task.id)) {
          status = 'in_progress';
        }

        groups[sectionName].push({
          id: task.id,
          _id: task._id,
          task_id: task.id,
          title: task.title,
          description: task.description,
          icon: getIconComponent(task.icon),
          backgroundColor: task.backgroundColor,
          iconColor: task.iconColor,
          status,
          sectionId: section._id,
          sectionName: section.name,
          sectionColors: section.colors,
          order: task.order,
          agent: task.agent
        });
      });
    });

    return groups;
  }, [sections, isTaskCompleted, hasConversation]);

  // Handle tile click
  const handleTileClick = useCallback((tile) => {
    const tileId = tile.id || tile.task_id || tile._id;

    // Check if conversation exists
    if (hasConversation(tileId)) {
      // Navigate to existing conversation
      window.open(`/projects/${projectId}/conversation?task=${tileId}`, '_blank');
    } else {
      // Show orchestrator dialog for new conversation
      setSelectedTaskForOrchestrator(tile);
      setShowOrchestratorDialog(true);
    }
  }, [projectId, hasConversation]);

  // Handle menu open
  const handleMenuOpen = useCallback((event, tile) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedTile(tile);
  }, []);

  // Handle menu close
  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setSelectedTile(null);
  }, []);

  // Handle edit (TaskMenu calls onClose after this)
  const handleEdit = useCallback(() => {
    if (selectedTile) {
      const taskId = selectedTile.id || selectedTile.task_id;
      window.open(`/projects/${projectId}/prd-workflow/edit?step=3&type=${taskId}`, '_blank');
    }
  }, [selectedTile, projectId]);

  // Handle mark as done (TaskMenu calls onClose after this)
  const handleMarkAsDone = useCallback(async () => {
    if (selectedTile) {
      const taskId = selectedTile.id || selectedTile.task_id;
      try {
        await toggleTaskStatus(taskId);
        showNotification?.({ message: 'Task status updated', type: 'success' });
      } catch (err) {
        showNotification?.({ message: 'Failed to update task status', type: 'error' });
      }
    }
  }, [selectedTile, toggleTaskStatus, showNotification]);

  // Handle download (placeholder) - TaskMenu calls onClose after this
  const handleDownloadPDF = useCallback(() => {
    showNotification?.({ message: 'PDF download coming soon', type: 'info' });
  }, [showNotification]);

  const handleDownloadDOCX = useCallback(() => {
    showNotification?.({ message: 'DOCX download coming soon', type: 'info' });
  }, [showNotification]);

  // Handle remove (placeholder) - TaskMenu calls onClose after this
  const handleRemove = useCallback(() => {
    showNotification?.({ message: 'Remove task coming soon', type: 'info' });
  }, [showNotification]);

  if (isLoading) return <LoadingView />;
  if (error) return <ErrorView error={error} onRetry={refetch} />;

  const tilesCount = Object.values(groupedTiles).flat().length;
  if (tilesCount === 0) {
    return <EmptyStateView onAddTopics={onOpenTaskSelector || (() => {})} />;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        pr: 4,
        pt: 0,
        pb: 0,
        m: 0,
        height: contentHeight,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '8px', backgroundColor: 'transparent' },
        '&::-webkit-scrollbar-track': { borderRadius: '10px', backgroundColor: 'rgba(0, 0, 0, 0.05)' },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: '10px',
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.4)' }
        }
      }}
    >
      <Box sx={{ width: '100%', maxWidth: { xs: '1600px', xl: '1800px' }, mx: 'auto', px: 0 }}>
        {Object.entries(groupedTiles).map(([sectionName, sectionTiles], sectionIndex) => {
          const section = sections.find(s => s.name === sectionName);

          return (
            <React.Fragment key={sectionName}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', md: 'row' },
                  gap: { xs: 2, md: 4 },
                  mb: { xs: 2, md: 0 },
                  mt: 0
                }}
              >
                <SectionHeader
                  sectionName={sectionName}
                  sectionId={section?._id}
                  projectId={projectId}
                  consolidatedAvatar={{ url: section?.orchestrator?.avatarUrl, name: section?.orchestrator?.name }}
                  onOrchestratorUpdate={updateOrchestrator}
                />

                <TasksGrid
                  sectionTiles={sectionTiles}
                  onTileClick={handleTileClick}
                  onMenuOpen={handleMenuOpen}
                />
              </Box>

              {sectionIndex !== Object.keys(groupedTiles).length - 1 && (
                <div className="section-divider" />
              )}
            </React.Fragment>
          );
        })}
      </Box>

      <TaskMenu
        anchorEl={anchorEl}
        selectedTile={selectedTile}
        onClose={handleMenuClose}
        onEdit={handleEdit}
        onDownloadPDF={handleDownloadPDF}
        onDownloadDOCX={handleDownloadDOCX}
        onMarkAsDone={handleMarkAsDone}
        onRemove={handleRemove}
        showNotification={showNotification}
      />

      {showOrchestratorDialog && selectedTaskForOrchestrator && (
        <AgentDialog
          open={showOrchestratorDialog}
          onClose={() => setShowOrchestratorDialog(false)}
          task={selectedTaskForOrchestrator}
          user={user}
          projectId={projectId}
          sectionAvatars={sectionAvatars}
        />
      )}
    </Box>
  );
};

Foundation.propTypes = {
  projectId: PropTypes.string.isRequired,
  showNotification: PropTypes.func,
  onOpenTaskSelector: PropTypes.func
};

export default Foundation;
